import { reactive, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAtomicSetter } from '@/composables/useAtomicSetter.js';
import { STATEMENTS_DEFAULT_DATE_FIELD } from '@/constants/dateFields.js';

const useStatementsStore = defineStore('statements', () => {
  const filters = reactive({
    search_text: '',
    page: 1,
    per_page: 25,
    status: [],
    date: STATEMENTS_DEFAULT_DATE_FIELD,
    date_from: '',
    date_to: '',
    currency: '',
  });

  const shouldResetFilters = ref(true);

  const stateRefs = {
    shouldResetFilters,
  };

  const { setAtomic } = useAtomicSetter(stateRefs);

  const setFilters = (newFilters) => {
    Object.assign(filters, newFilters);
  };

  // Clears the structured filters, not search_text - used by the explicit "Clear filters" action.
  const resetAllFilters = () => {
    Object.assign(filters, {
      page: 1,
      status: [],
      date: STATEMENTS_DEFAULT_DATE_FIELD,
      date_from: '',
      date_to: '',
      currency: '',
    });
  };

  watch(
    () => filters.search_text,
    (newSearchText, oldSearchText) => {
      if (newSearchText !== oldSearchText) {
        setFilters({ page: 1 });
      }
    }
  );

  // Filters auto-apply as soon as they change (no explicit "Apply" step), so any change to
  // one should re-page back to 1, same as a new search term does above.
  watch(
    () => [filters.status, filters.date, filters.date_from, filters.date_to, filters.currency],
    () => setFilters({ page: 1 }),
    { deep: true }
  );

  // Used by useFiltersReset() on a fresh visit - only clears the search box, leaving the
  // sticky structured filters above untouched (those only clear via "Clear filters").
  const resetFilters = () => {
    Object.assign(filters, { search_text: '', page: 1 });
  };

  return { filters, resetFilters, resetAllFilters, setFilters, ...stateRefs, setAtomic };
});

const useStatementReservationsStore = defineStore('statementReservations', () => {
  const filters = reactive({
    status: 'confirmed',
    search_text: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    per_page: 25,
  });

  const setFilters = (newFilters) => {
    Object.assign(filters, newFilters);
  };

  const resetFilters = () => {
    Object.assign(filters, { page: 1, search_text: '' });
  };

  watch(
    () => filters.search_text,
    (newSearchText, oldSearchText) => {
      if (newSearchText !== oldSearchText) {
        setFilters({ page: 1 });
      }
    }
  );

  return { filters, setFilters, resetFilters };
});

const useStatementTransfersStore = defineStore('statementTransfers', () => {
  const filters = reactive({
    status: 'all',
    search_text: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    per_page: 25,
  });

  const setFilters = (newFilters) => {
    Object.assign(filters, newFilters);
  };

  const resetFilters = () => {
    Object.assign(filters, { page: 1, search_text: '' });
  };

  watch(
    () => filters.search_text,
    (newSearchText, oldSearchText) => {
      if (newSearchText !== oldSearchText) {
        setFilters({ page: 1 });
      }
    }
  );

  return { filters, setFilters, resetFilters };
});

const useStatementsSummaryStore = defineStore('statementsSummary', () => {
  const activeTab = ref('status');
  const setActiveTab = (tab) => {
    activeTab.value = tab;
  };
  return { activeTab, setActiveTab };
});

const useStatementFiltersDrawerStore = defineStore('statement-filters-drawer', () => {
  const isOpen = ref(false);

  const openDrawer = () => {
    isOpen.value = true;
  };

  const closeDrawer = () => {
    isOpen.value = false;
  };

  return { isOpen, openDrawer, closeDrawer };
});

export {
  useStatementsStore,
  useStatementReservationsStore,
  useStatementTransfersStore,
  useStatementsSummaryStore,
  useStatementFiltersDrawerStore,
};
